import { Filters, Header, Search, Select, useBreakpoints, useLoading, ValueOpt } from "best-common-react";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { getRequestOnBehalfOf, getUsersRequests } from "../../api/RequesTixApi";
import MyRequestsTable from "../../components/requests/personal/MyRequestsTable";
import { useAuth } from "../../contexts/AuthContext";
import { useDropdowns } from "../../contexts/DropdownsContext";
import { RequestCategory } from "../../types/RequestType";
import { User } from "../../types/User";
import { UserTicketRequestDTO } from "../../types/UserRequest";
import { useUrlParams } from "../../util/RouteUtil";
import { requestsFreeTextSearch } from "../../util/TicketRequestUtil";

const RequestsPage = () => {
  const location = useLocation();
  const { isCoordinator, userInfo } = useAuth();
  const { setLoading } = useLoading();
  const { isMobile } = useBreakpoints();
  const { requestCategoryOptions, gameOptions, requestStateFilterOptions, getOptionByValue } = useDropdowns();
  const [{ games, status, category, gamePk }, setParam] = useUrlParams({
    games: undefined,
    status: "",
    category: undefined,
    gamePk: undefined,
  });
  const [onBehalfOf, setOnBehalfOf] = useState<User | undefined>(undefined);
  const [onBehalfOfOptions, setOnBehalfOfOptions] = useState<ValueOpt<User>[]>([]);
  const [requests, setRequests] = useState<UserTicketRequestDTO[]>([]);
  const [displayRequests, setDisplayRequests] = useState<UserTicketRequestDTO[]>([]);
  const [search, setSearch] = useState<string>("");
  const [activeGamePk, setActiveGamePk] = useState<number | undefined>(undefined);

  const fetchUsersRequests = async (userId: number | null) => {
    try {
      if (userId) {
        setLoading(true);
        const data: UserTicketRequestDTO[] = await getUsersRequests(
          userId,
          userId,
          games ? Number(games) : undefined,
          category ? Number(category) : undefined,
          status,
        );
        setRequests(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchOnBehalfOf = async () => {
    try {
      setLoading(true);
      const data: User[] = await getRequestOnBehalfOf(userInfo!.employeeUserId);
      const options = data.map((user: User) => ({
        label: `${user.lastName}, ${user.firstName}`,
        value: user,
      }));
      setOnBehalfOfOptions(options);
      const selfOption = options.find((opt: ValueOpt<User>) => opt.value.employeeUserId === userInfo!.employeeUserId);
      if (selfOption) {
        setOnBehalfOf(selfOption.value);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCoordinator && userInfo?.employeeUserId) {
      void fetchOnBehalfOf();
    }
  }, [isCoordinator]);

  useEffect(() => {
    const userId = isCoordinator && onBehalfOf ? onBehalfOf.employeeUserId : userInfo ? userInfo.employeeUserId : null;
    void fetchUsersRequests(userId);
  }, [userInfo, onBehalfOf, games, status, category]);

  useEffect(() => {
    if (gamePk && requests.length) {
      setActiveGamePk(Number(gamePk));
      setTimeout(() => {
        setActiveGamePk(undefined);
        setParam("gamePk", undefined);
      }, 3000);
    }
  }, [gamePk, requests]);

  useEffect(() => {
    setDisplayRequests(requestsFreeTextSearch(requests, search));
  }, [requests, search]);

  return (
    <div>
      <Header title="My Requests" flexColumn>
        <Filters
          actionIconPosition="absolute"
          numberDisplayed={isMobile ? 1 : 3}
          indexesToDisplay={isMobile ? [1] : undefined}
        >
          {
            (isCoordinator && (
              <Select
                id="onBehalfOf"
                label="Requested on Behalf of"
                options={onBehalfOfOptions}
                value={getOptionByValue(onBehalfOfOptions, onBehalfOf)}
                onChange={(value: ValueOpt<User>) => setOnBehalfOf(value.value)}
                gutterBottom
              />
            )) as React.ReactElement
          }
          <Select
            id="games"
            label="Games"
            options={gameOptions}
            value={gameOptions.find((option) => games !== undefined && option.value === Number(games))}
            onChange={({ value }: ValueOpt<number>) => setParam("games", String(value))}
            gutterBottom
          />
          <Select
            id="status"
            label="Status"
            options={requestStateFilterOptions}
            value={requestStateFilterOptions.find((option) => option.value.join() === status)}
            onChange={({ value }: ValueOpt<number[]>) => setParam("status", value.join())}
            gutterBottom
          />
          <Select
            id="category"
            label="Category"
            options={requestCategoryOptions}
            value={requestCategoryOptions.find(({ value }) => value.requestCategoryId == category)}
            onChange={({ value }: ValueOpt<RequestCategory>) => setParam("category", String(value.requestCategoryId))}
            gutterBottom
          />
          <Search
            id="my-requests-search"
            label="Search Requests"
            value={search}
            onChange={(value: string) => setSearch(value)}
            placeholder="begin typing to filter requests"
            gutterBottom
          />
        </Filters>
      </Header>
      <div className="bcr-p-2">
        <MyRequestsTable
          requests={displayRequests}
          location={location}
          size={requests.length}
          activeGamePk={activeGamePk}
        />
      </div>
    </div>
  );
};

export default RequestsPage;
